import React from "react";
import {
  Card,
  CardHeader,
  Body1,
  Link,
  Switch,
  makeStyles,
  makeStaticStyles,
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { ArrowEjectRegular, Info20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useStore, log } from "../index";
import { IClausionStateStore } from "../../zustand/store";
import { useShallow } from "zustand/shallow";
import globalStyles from "./CommonStyle";
import { useMsal } from "@azure/msal-react";

// Component styles
const useStyles = makeStyles({
  root: {
    borderRadius: "0em !important",
    boxShadow: "none",
    marginBottom: "50px",
    paddingTop: "0px",
  },
});

const LoggingCard: React.FC = () => {
  const globalStyle = globalStyles();
  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const classes = useStyles();
  const { accounts } = useMsal();
  const isLoggingEnabled = useStore(useShallow((state: IClausionStateStore) => state.isLoggingEnabled));
  const refreshMode = useStore(useShallow((state: IClausionStateStore) => state.refreshMode));
  const serviceUrl = useStore(useShallow((state: IClausionStateStore) => state.serviceUrl));
  const automaticCalculation = useStore(useShallow((state: IClausionStateStore) => state.automaticCalculation));
  const [setIsLoggingEnabled] = useStore(useShallow((state: IClausionStateStore) => [state.setIsLoggingEnabled]));

  const toggleLogging = () => {
    setIsLoggingEnabled(!isLoggingEnabled);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    if (isTooltipVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTooltipVisible]);

  React.useEffect(() => {
    if (isLoggingEnabled) {
      log.enable();
      log.info(
        "Logging enabled",
        `Refresh Mode: ${refreshMode}`,
        `Automatic Calculation: ${automaticCalculation}`,
        `Service address: ${serviceUrl}`,
        `Logged In User: ${accounts[0]?.username}`,
      );
    } else {
      log.info("Logging disabled");
      log.disable();
    }
  }, [isLoggingEnabled]);

  return (
    <Card className={classes.root}>
      <CardHeader
        header={
          <Body1 className={globalStyle.headerContainer}>
            <span className={globalStyle.titleContainer}>
              <ArrowEjectRegular />
              <span className={`${globalStyle.fontStyle},${globalStyle.font16}`}>{t("logging_title")}</span>
            </span>

            <Popover>
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  ref={buttonRef} // Attach ref to the button
                  appearance="transparent"
                  icon={<Info20Regular />}
                  size="small" // Reduce button size
                  onClick={() => setIsTooltipVisible(!isTooltipVisible)} // Toggle tooltip visibility
                />
              </PopoverTrigger>

              <PopoverSurface className={globalStyle.popoverSurface}>
                <div>
                  <p>
                    {t("logging_text")}{" "}
                  </p>
                </div>
              </PopoverSurface>
            </Popover>
          </Body1>
        }
      />
      <div className={globalStyle.switchContainer}>
        <span className={globalStyle.label}>{isLoggingEnabled ? t("logging_enabled") : t("logging_disabled")}</span>
        <Switch checked={isLoggingEnabled} onChange={toggleLogging} />
      </div>
    </Card>
  );
};

export default LoggingCard;
